import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { ReportButton } from '@/features/reports/ReportButton';
import {
  blockedActionReason,
  blockedMessageReason,
  restrictionNotice,
} from '@/features/applications/accountStanding';
import {
  BlockedAction,
  CandidateRestrictionBadge,
} from '@/features/applications/CandidateRestriction';
import { NextStepPanel } from '@/features/applications/NextStepPanel';
import { RecruitmentProgress } from '@/features/applications/RecruitmentProgress';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { MatchBadge } from '@/features/matching/MatchBadge';
import { MatchBreakdown } from '@/features/matching/MatchBreakdown';
import { CloseDialog } from '@/features/interviews/CloseDialog';
import {
  InterviewStandingBadge,
  InterviewStandingNote,
} from '@/features/interviews/InterviewStanding';
import { useRescheduleAnswer } from '@/features/interviews/useRescheduleAnswer';
import { RescheduleRequestPanel } from '@/features/interviews/RescheduleRequestPanel';
import { canRecordOutcome, hasOpenRequest } from '@/features/interviews/reschedule';
import { ScheduleDialog } from '@/features/interviews/ScheduleDialog';
import { RoundNotes } from '@/features/interviews/RoundNotes';
import { OfferDialog } from '@/features/offers/OfferDialog';
import { OfferLetterControls } from '@/features/offers/OfferLetterControls';
import { OFFER_LABELS, OFFER_TONES, stipendLine } from '@/features/offers/status';
import { INTERVIEW_LABELS, INTERVIEW_TONES, interviewWhen } from '@/features/interviews/status';
import { ApplicationThread } from '@/features/messages/ApplicationThread';
import { PremiumBadge } from '@/features/studentBilling/PremiumBadge';
import { historyTitle } from '@/features/applications/history';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

/** The banner's heading, by the candidate's account status. */
const RESTRICTED_TITLES = {
  SUSPENDED: 'Candidate account suspended',
  BANNED: 'Candidate account banned',
  INACTIVE: 'Candidate account deactivated',
};

/** One entry in a rail list -- an interview round, an offer. */
const ENTRY = 'border-line rounded-input flex gap-3 border p-3';
const ENTRY_ICON =
  'bg-brand-50 text-brand-600 ring-brand-100 grid size-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset';

export function CompanyApplicationDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  // The round number to book, or null when the dialog is shut.
  const [scheduling, setScheduling] = useState(null);
  const [offering, setOffering] = useState(false);
  const [closingRound, setClosingRound] = useState(null);
  const [movingRound, setMovingRound] = useState(null);
  const [cancellingRound, setCancellingRound] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const answer = useRescheduleAnswer();
  const [joining, setJoining] = useState(false);
  const [joinNote, setJoinNote] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-application', id],
    queryFn: () => companyApi.getApplication(id),
  });

  // Rounds already booked against this application, so the panel can see the
  // history without leaving the applicant.
  // Interviews, offers and messages are separate permissions; a member
  // without one simply does not see that part of the application.
  const { can } = usePermissions();
  const { data: interviews } = useQuery({
    queryKey: ['company-interviews', { application_id: id }],
    queryFn: () => companyApi.listInterviews({ application_id: id, page_size: 20 }),
    enabled: can(PERMISSIONS.INTERVIEW_CREATE),
  });

  const { data: offers } = useQuery({
    queryKey: ['company-offers', { application_id: id }],
    queryFn: () => companyApi.listOffers({ application_id: id, page_size: 20 }),
    enabled: can(PERMISSIONS.OFFER_CREATE),
  });

  useEffect(() => {
    if (data) setNote(data.internal_note ?? '');
  }, [data]);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-application', id] });
    void queryClient.invalidateQueries({ queryKey: ['company-applications'] });
    void queryClient.invalidateQueries({ queryKey: ['company-application-counts'] });
  }

  const move = useMutation({
    mutationFn: (input) => companyApi.changeApplicationStatus(id, input),
    onSuccess: (updated) => {
      toast.success(
        updated.status === 'HIRED'
          ? 'Marked as joined. Hiring complete.'
          : `Moved to ${APPLICATION_LABELS[updated.status] ?? updated.status}`,
      );
      invalidate();
      setRejecting(false);
      setRejectionReason('');
      setJoining(false);
      setJoinNote('');
    },
    onError: (moveError) =>
      toast.error(errorMessage(moveError, 'Could not update the application')),
  });

  const sendDraft = useMutation({
    mutationFn: (offer) => companyApi.sendOffer(offer.id),
    onSuccess: () => {
      toast.success('Offer sent. The candidate has been notified.');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['company-offers'] });
      void queryClient.invalidateQueries({ queryKey: ['offer-counts'] });
    },
    onError: (sendError) => toast.error(errorMessage(sendError, 'Could not send the offer')),
  });

  const cancelRound = useMutation({
    mutationFn: ({ id: roundId, reason }) => companyApi.cancelInterview(roundId, reason),
    onSuccess: () => {
      toast.success('Interview cancelled. The candidate has been told.');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['company-interviews'] });
      void queryClient.invalidateQueries({ queryKey: ['interview-counts'] });
      setCancellingRound(null);
      setCancelReason('');
    },
    onError: (cancelError) => toast.error(errorMessage(cancelError, 'Could not cancel')),
  });

  const saveNote = useMutation({
    mutationFn: (value) => companyApi.setApplicationNote(id, value),
    onSuccess: () => {
      toast.success('Note saved');
      invalidate();
    },
    onError: (noteError) => toast.error(errorMessage(noteError, 'Could not save the note')),
  });

  if (isPending) return <LoadingState label="Loading the application…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  // Latest round first (5, 4, 3…), by round number rather than date: a
  // rescheduled round can fall after a later one by date.
  const rounds = [...(interviews?.data ?? [])].sort((a, b) => b.round_number - a.round_number);
  const madeOffers = offers?.data ?? [];
  // History arrives oldest first, so the last entry is where it stands now.
  const history = data.history ?? [];
  const attendedRounds = rounds.filter(
    (round) => round.status === 'COMPLETED' || round.status === 'NO_SHOW',
  ).length;
  // Join-Wonder suspended, banned or deactivated the candidate: the record is
  // all here, but nothing can be moved, booked, offered or sent.
  const blocked = blockedActionReason(data);
  const notice = restrictionNotice(data);

  // What each "Next step" button does. The panel decides which are shown.
  const actions = {
    shortlist: () => move.mutate({ status: 'SHORTLISTED' }),
    review: () => move.mutate({ status: 'UNDER_REVIEW' }),
    reject: () => setRejecting(true),
    schedule: (round) => setScheduling(round),
    offer: () => setOffering(true),
    sendOffer: (offer) => sendDraft.mutate(offer),
    closeRound: (round) => setClosingRound(round),
    moveRound: (round) => setMovingRound(round),
    cancelRound: (round) => setCancellingRound(round),
    acceptRequest: (round) => answer.accept(round),
    declineRequest: (round) => answer.decline(round),
    joined: () => setJoining(true),
  };
  const busy = {
    shortlist: move.isPending && move.variables?.status === 'SHORTLISTED',
    review: move.isPending && move.variables?.status === 'UNDER_REVIEW',
    sendOffer: sendDraft.isPending,
  };

  return (
    <div>
      <RecordHeader
        backTo={paths.company.applications}
        backLabel="Back to applications"
        eyebrow="Application"
        title={data.student.full_name}
        subtitle={`Applied to ${data.internship_title} on ${formatDate(data.created_at)}`}
        badges={
          <>
            <Badge dot tone={APPLICATION_TONES[data.status] ?? 'neutral'}>
              {APPLICATION_LABELS[data.status] ?? data.status}
            </Badge>
            {data.is_premium && <PremiumBadge />}
            <CandidateRestrictionBadge row={data} />
          </>
        }
        actions={
          <>
            <ReportButton
              targetType="STUDENT"
              targetId={data.student.id}
              targetName={data.student.full_name}
            />
            {data.resume?.download_url && (
              <a
                href={resolveFileUrl(data.resume.download_url)}
                target="_blank"
                rel="noreferrer noopener"
                className="border-line bg-surface text-ink rounded-input hover:border-brand-200 hover:bg-brand-50/60 inline-flex h-10 max-w-full items-center gap-2 border px-4 text-sm font-medium shadow-xs transition"
              >
                <Icon name="download" size={16} className="shrink-0" />
                <span className="truncate">Download {data.resume.file_name}</span>
              </a>
            )}
          </>
        }
      />

      {notice && (
        <Alert
          tone="error"
          title={RESTRICTED_TITLES[data.candidate_account_status]}
          className="mb-6"
        >
          {notice}
        </Alert>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title="Recruitment progress"
              description="Shortlist, interview as many rounds as you need, offer, and confirm when they join."
            />
            <RecruitmentProgress status={data.status} history={history} rounds={attendedRounds} />
          </Card>

          {data.match && (
            <Card>
              <CardHeader
                title="Fit for this role"
                description="Scored when they applied, and kept as it was — the profile may have changed since."
                action={<MatchBadge match={data.match} size="lg" />}
              />
              {/* `advice` off: what the student could add to their own profile
                  is between them and the platform. Location is hidden from the
                  list here (it still counts toward the score). */}
              <MatchBreakdown match={data.match} advice={false} hide={['location']} compact />
            </Card>
          )}

          <Card>
            <CardHeader title="Cover letter" />
            {data.cover_letter ? (
              <p className="text-ink-muted text-sm leading-6 whitespace-pre-line">
                {data.cover_letter}
              </p>
            ) : (
              <EmptyState compact title="No cover letter submitted." />
            )}
          </Card>

          {data.answers && (
            <Card>
              <CardHeader title="Answers" />
              <p className="text-ink-muted text-sm leading-6 whitespace-pre-line">{data.answers}</p>
            </Card>
          )}

          <Card>
            <CardHeader
              title="Internal note"
              description="Only your team can see this. The applicant never does."
            />
            <Textarea
              rows={4}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Interview feedback, salary band, anything worth remembering."
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                isLoading={saveNote.isPending}
                onClick={() => saveNote.mutate(note)}
              >
                Save note
              </Button>
            </div>
          </Card>

          {can(PERMISSIONS.MESSAGE_READ) && (
            <ApplicationThread
              applicationId={data.id}
              conversationId={data.conversation_id}
              candidateName={data.student.full_name}
              disabledReason={blockedMessageReason(data)}
            />
          )}

          <Card>
            <CardHeader title="History" />
            <ol className="relative space-y-5 pl-6">
              <span
                aria-hidden="true"
                className="bg-line absolute top-1.5 bottom-1.5 left-[5px] w-px"
              />
              {history.map((entry, index) => {
                const isCurrent = index === history.length - 1;
                return (
                  <li key={`${entry.to_status}-${index}`} className="relative text-sm">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute top-1 -left-6 size-[11px] rounded-full',
                        isCurrent ? 'bg-brand-600 ring-brand-100 ring-4' : 'bg-ink-muted/40',
                      )}
                    />
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span
                        className={cn('font-medium', isCurrent ? 'text-ink' : 'text-ink-muted')}
                      >
                        {historyTitle(entry, (status) => APPLICATION_LABELS[status] ?? status)}
                      </span>
                      <span className="text-ink-muted text-xs">{formatDate(entry.created_at)}</span>
                    </div>
                    {entry.note && <p className="text-ink-muted mt-0.5 text-xs">{entry.note}</p>}
                  </li>
                );
              })}
            </ol>
          </Card>
        </div>

        {/* First on a phone: the next step is why someone opens this page. */}
        <div className="order-first min-w-0 space-y-6 lg:order-none">
          <NextStepPanel
            application={data}
            rounds={rounds}
            offers={madeOffers}
            actions={actions}
            busy={busy}
            blockedReason={blocked}
          />

          <Card>
            <CardHeader
              title="Interview rounds"
              description={
                rounds.length > 0
                  ? 'One round at a time, each one closed before the next.'
                  : undefined
              }
            />
            {rounds.length === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="calendar" size={16} />}
                title="No rounds yet. Book one from the next step above."
              />
            ) : (
              <div
                // About three rounds tall; the rest scroll inside the card, latest on top.
                // The scroller wraps the list so the timeline rail spans every round,
                // not just the visible height.
                className="max-h-[30rem] overflow-y-auto overscroll-contain pr-1"
                tabIndex={0}
                aria-label="Interview rounds"
              >
                <ol className="before:bg-line relative space-y-5 pl-6 before:absolute before:top-1 before:bottom-1 before:left-2 before:w-px">
                  {rounds.map((interview) => (
                    <li key={interview.id} className="relative flex gap-3">
                      <span
                        aria-hidden="true"
                        className="bg-brand-500 ring-surface absolute top-1 -left-[21.5px] size-3 rounded-full ring-4"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-ink text-sm font-semibold">
                          Round {interview.round_number} · {interview.title}
                        </p>
                        <p className="text-ink-muted mt-0.5 text-xs">{interviewWhen(interview)}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <Badge dot tone={INTERVIEW_TONES[interview.status] ?? 'neutral'}>
                            {INTERVIEW_LABELS[interview.status] ?? interview.status}
                          </Badge>
                          <InterviewStandingBadge interview={interview} />
                          {interview.feedback_count > 0 && (
                            <span className="text-ink-muted text-xs">
                              {interview.average_rating}/5
                              {interview.feedback_count > 1 &&
                                ` average of ${interview.feedback_count}`}
                            </span>
                          )}
                        </div>
                        <RoundNotes interview={interview} />
                        <InterviewStandingNote interview={interview} />
                        {hasOpenRequest(interview) && (
                          <RescheduleRequestPanel
                            request={interview.reschedule_request}
                            candidateName={data.student.full_name}
                            disabledReason={blocked}
                            onUseSlot={(slot) => answer.accept(interview, slot)}
                            actions={
                              <>
                                <BlockedAction reason={blocked}>
                                  <Button
                                    variant="tintDanger"
                                    size="sm"
                                    title="Declining cancels this interview"
                                    disabled={Boolean(blocked)}
                                    onClick={() => answer.decline(interview)}
                                  >
                                    <Icon name="close" size={14} />
                                    Decline
                                  </Button>
                                </BlockedAction>
                                <BlockedAction reason={blocked}>
                                  <Button
                                    variant="tintPositive"
                                    size="sm"
                                    disabled={Boolean(blocked)}
                                    onClick={() => answer.accept(interview)}
                                  >
                                    <Icon name="check" size={14} />
                                    Accept
                                  </Button>
                                </BlockedAction>
                              </>
                            }
                          />
                        )}
                      </div>
                      {canRecordOutcome(interview) && (
                        <BlockedAction reason={blocked} className="inline-flex h-fit">
                          <IconButton
                            icon="check"
                            variant="ghostPositive"
                            label={`Record the outcome of round ${interview.round_number}`}
                            disabled={Boolean(blocked)}
                            onClick={() => setClosingRound(interview)}
                          />
                        </BlockedAction>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Offers" />
            {madeOffers.length === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="document" size={16} />}
                title="No offer yet. Make one from the next step above."
              />
            ) : (
              <ol className="space-y-3">
                {madeOffers.map((offer) => (
                  <li key={offer.id} className={ENTRY}>
                    <span aria-hidden="true" className={ENTRY_ICON}>
                      <Icon name="document" size={16} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-ink text-sm font-semibold">{offer.title}</p>
                      <p className="text-ink-muted mt-0.5 text-xs">{stipendLine(offer)}</p>
                      <Badge dot tone={OFFER_TONES[offer.status] ?? 'neutral'} className="mt-2">
                        {OFFER_LABELS[offer.status] ?? offer.status}
                      </Badge>
                      {/* The letter the candidate gets: preview it, and while the
                          offer is open, swap in a signed PDF. */}
                      <OfferLetterControls
                        offer={offer}
                        canChange={can(PERMISSIONS.OFFER_UPDATE) && !blocked}
                        prominent
                      />
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card>
            <CardHeader title="Applicant" />
            <dl className="divide-line divide-y text-sm">
              <Fact label="Email" value={data.student.email} />
              {data.student.university && (
                <Fact label="University" value={data.student.university} />
              )}
              {data.student.location && <Fact label="Location" value={data.student.location} />}
              <Fact label="Profile completion" value={`${data.student.profile_completion}%`} />
            </dl>
          </Card>
        </div>
      </div>

      {offering && (
        <OfferDialog
          open
          onClose={() => setOffering(false)}
          applicationId={id}
          defaultTitle={data.internship_title}
        />
      )}

      {scheduling && (
        <ScheduleDialog
          open
          onClose={() => setScheduling(null)}
          applicationId={id}
          roundNumber={scheduling}
        />
      )}

      {closingRound && (
        <CloseDialog interview={closingRound} onClose={() => setClosingRound(null)} />
      )}

      {movingRound && (
        <ScheduleDialog open interview={movingRound} onClose={() => setMovingRound(null)} />
      )}

      {answer.dialogs}

      <Modal
        open={Boolean(cancellingRound)}
        title={`Cancel "${cancellingRound?.title}"?`}
        description="The candidate sees the reason you give."
        onClose={() => setCancellingRound(null)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            required
            placeholder="The role has been put on hold."
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCancellingRound(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={cancelReason.trim().length < 4}
              isLoading={cancelRound.isPending}
              onClick={() =>
                cancelRound.mutate({ id: cancellingRound.id, reason: cancelReason.trim() })
              }
            >
              Cancel interview
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={joining}
        title="Mark as joined"
        description="Confirm the candidate has started with you. This completes the hire and closes the application."
        onClose={() => setJoining(false)}
      >
        <div className="space-y-4">
          <Input
            label="Note (optional)"
            placeholder="Started on Monday with the platform team"
            value={joinNote}
            onChange={(event) => setJoinNote(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setJoining(false)}>
              Cancel
            </Button>
            <Button
              variant="success"
              isLoading={move.isPending}
              onClick={() => move.mutate({ status: 'HIRED', note: joinNote || undefined })}
            >
              Confirm joined
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={rejecting}
        title="Reject application"
        description="The reason is shown to the applicant, so keep it constructive. Any booked round is cancelled and any open offer withdrawn."
        onClose={() => setRejecting(false)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            placeholder="Looking for more backend experience"
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={move.isPending}
              onClick={() =>
                move.mutate({
                  status: 'REJECTED',
                  rejection_reason: rejectionReason || undefined,
                })
              }
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0">
      <dt className="text-ink-muted text-xs">{label}</dt>
      <dd className="text-ink break-words">{value}</dd>
    </div>
  );
}
